import React, { useContext, useEffect, useLayoutEffect, useState } from 'react';
import { matchPath, useLocation, useNavigate, useParams } from 'react-router-dom';

import { useAppDispatch, useAppSelector } from 'hooks/store';

import { RootState } from 'store';
import { setUserData } from 'store/authorization';
import { ThunkGetUser } from 'store/user/thunks';
import { ThunkGetVerificationSettings } from 'store/verificationSettings/thunks';

import { REFRESH_TOKEN, TOKEN, USER_ID } from 'constants/localStorage';

import Loader from 'components/Loader';
import Sidebar from 'components/Sidebar';

import { PusherEvents } from 'constants/pusher';
import { SignalREvents } from 'constants/signalR';
import { PusherContext } from 'contexts/pusher';
import { useSignalREvent } from 'hooks/signalR';
import {
  AUTOTASK_POD,
  AUTOTASK_POD_WIDGET,
  FRESH_SERVICE_POD,
  FRESH_SERVICE_POD_WIDGET,
} from 'router/routes';
import { addClientPortalChat } from 'store/clientPortalChats';
import { setConfigurationData } from 'store/configuration';
import { ThunkGetMyPricePlan } from 'store/pricePlan/thunks';
import { addOnTop } from 'store/table';
import { UserDataSelector } from 'store/user';
import { SESSION_STORAGE_URL } from '../../constants/sessionStorage';

interface IProps {
  children: React.ReactNode;
  noSidebar?: boolean;
}

interface IPSATechVerification {
  TenantId: number;
  Id: number;
  From: string;
  To: string;
  CreatedDate: string;
  Code: string;
  ContactId: number;
  Type: string;
  CrmId: number;
  ContactName: string;
  IsVerified: boolean;
}

interface IMetadata {
  Id: string;
  CrmId: string;
  Name: string;
  IsClosed: boolean;
  TicketId: string;
  TicketLink: string;
}

interface INewClientPortalChat {
  chatId: number;
  dateTime: string;
  message: string;
  metadata: string;
  type: 0;
  userName: 'TestContact TestContact';
}
const Layout: React.FC<IProps> = ({ children, noSidebar = false }) => {
  const [loading, setLoading] = useState<boolean>(true);
  const [showSidebar, setShowSidebar] = useState<boolean>(true);

  // * LOCAL STORAGE ITEMS
  const accessToken = localStorage.getItem(TOKEN);
  const refreshToken = localStorage.getItem(REFRESH_TOKEN);
  const userId = localStorage.getItem(USER_ID);
  const { pusher } = useContext(PusherContext);

  const tokens = useAppSelector((state: RootState) => state.user);
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const location = useLocation();

  const params = useParams();
  const searchParams = new URLSearchParams(location.search);
  const userData = useAppSelector(UserDataSelector);

  // Getting user data and verification settings
  const getBaseData = async (userId: number): Promise<void> => {
    setLoading(true);
    await dispatch(ThunkGetUser(+userId));
    await dispatch(ThunkGetVerificationSettings());
    setLoading(false);
  };

  useEffect(() => {
    dispatch(ThunkGetMyPricePlan());
  }, [userId]);

  useEffect(() => {
    if (!userId) {
      navigate('/');
    } else {
      getBaseData(+userId);
    }
  }, [userId]);

  useLayoutEffect(() => {
    // Restoring Previous Session
    if (!tokens && accessToken && refreshToken && userId) {
      dispatch(setUserData({ accessToken, refreshToken, userId }));
      return;
    }
    // Catching if tokens are not presented
    if (!tokens && !accessToken) {
      navigate('/');
    }
  }, [tokens]);

  useEffect(() => {
    // getting params from url and storing it to configurations
    if (params?.psaId) {
      const ticketId = searchParams.get('ticketId') ?? searchParams.get('entityid');
      const screenType = searchParams.get('screen');
      const configuration = {
        psaId: +params?.psaId,
        ticketId: ticketId?.replace(/^0+/, '') ?? undefined,
        screen: ['ticket', 'contact', 'project_ticket'].includes(screenType as string)
          ? (screenType as 'ticket' | 'contact' | 'project_ticket')
          : 'ticket',
        contactId:
          searchParams.get('screen') === 'contact'
            ? searchParams.get('ticketId') ?? undefined
            : undefined,
      };

      dispatch(setConfigurationData(configuration));
    }

    sessionStorage.setItem(SESSION_STORAGE_URL, `${location.pathname}${location.search}`);
  }, [location, searchParams]);

  useSignalREvent(SignalREvents.PsaTechVerification, data => {
    const res: IPSATechVerification = JSON.parse(data);
    dispatch(
      addOnTop({
        code: res.Code,
        contactCompanyId: null,
        contactCompanyName: null,
        contactId: res.ContactId,
        contactName: res.ContactName,
        createdDate: res.CreatedDate,
        crmId: res.CrmId,
        from: res.From,
        id: res.Id,
        isVerified: res.IsVerified,
        techAccountId: null,
        techAccountName: null,
        to: res.To,
        type: res.Type,
        verifiedDate: null,
      }),
    );
  });

  useEffect(() => {
    if (pusher && userData) {
      const channel = pusher.subscribe(`tenant-${userData.tenantId}`);

      channel.bind(PusherEvents.newClientPortalChat, (data: INewClientPortalChat) => {
        const metadata: IMetadata = JSON.parse(data.metadata);

        dispatch(
          addClientPortalChat({
            isClosed: false,
            name: metadata.Name,
            id: +metadata.Id,
            ticketId: metadata.TicketId,
            ticketNumber: undefined,
            ticketLink: metadata.TicketLink,
            contactName: '',
            assigneeName: '',
            isAnswered: false,
            lastMessage: '',
            lastMessageDate: '',
          }),
        );
      });
    }
  }, [pusher]);

  useLayoutEffect(() => {
    if (
      matchPath(AUTOTASK_POD, location.pathname) ??
      matchPath(AUTOTASK_POD_WIDGET, location.pathname) ??
      matchPath(FRESH_SERVICE_POD, location.pathname) ??
      matchPath(FRESH_SERVICE_POD_WIDGET, location.pathname)
    ) {
      setShowSidebar(false);
    }
  }, [location.pathname]);

  if (loading) return <Loader />;
  return (
    <div style={{ display: 'flex', height: '100%' }}>
      {showSidebar && <Sidebar />}
      {children}
    </div>
  );
};

export default Layout;
